//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// === STYLES ===
import { useTheme } from '@mui/material/styles';
import { Box, CircularProgress } from '@mui/material';

// === HOOKS ===
import { useLogout } from 'components/useLogout';
import { useInactiveTimeout } from 'components/hooks/useInactiveTimeout';
import { OnlineStatus, useSession } from './components/SessionContext';
import { useAlert } from 'components/hooks/useAlert/index';

// === API ===
import { prokeyApi } from 'api/prokey';
import { actions as displayActions } from 'store/modules/display';

// === COMPONENTS ===
import Styled from 'components/Styled/Styled';
import { Menu             } from 'pages/Menu';
import { TaskVerification } from 'pages/TaskVerification';
import { Admin            } from 'pages/Admin/Admin';
import { Stats            } from 'pages/Stats/Stats';
import { OcrOnDemand      } from 'pages/OcrOnDemand/OcrOnDemand';
import { WorkflowMonitor  } from 'pages/WorkflowMonitor';
import { DocAnalytics     } from 'pages/DocAnalytics/DocAnalytics';
import { TaskVerifyBar    } from 'pages/Menu/TaskVerifyBar/TaskVerifyBar';
import { AuditQC          } from 'pages/AuditQC';
import { FileViewer       } from 'pages/FileViewer/index';

import { Fields     } from 'pages/TaskVerification/components/TaskPanel/components/Fields/index';
import { Image      } from 'pages/TaskVerification/components/TaskPanel/components/Image/index';
import { Thumbnails } from 'pages/TaskVerification/components/TaskPanel/components/Thumbnails/index';

import { Login } from 'pages/Login/Login';


// === API ===
import { onInactive, onReconnectFail, removeListener } from 'api/prokey/prokey.websocket';

var pjson = require('../../../package.json');

//#endregion

//-----------------------------------------------------------------
export function PageManager() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const logout   = useLogout();
  const theme    = useTheme();
  const Alert    = useAlert();
  const dispatch = useDispatch();
  const location = useLocation();
  
  useInactiveTimeout();

  const { online_status } = useSession();

  const is_online = online_status === OnlineStatus.CONNECTED;

  const PAGE_OCROD      = 'onDemand'         ;
  const PAGE_VERIFY     = 'verify'           ;
  const PAGE_VERIFY_FIELDS = 'verify-fields' ;
  const PAGE_VERIFY_IMAGES = 'verify-images' ;
  const PAGE_VERIFY_THUMBS = 'verify-thumbs' ;
  const PAGE_WFMON      = 'workflow-monitor' ;
  const PAGE_STATS      = 'stats'            ;
  const PAGE_ADMIN      = 'admin'            ;
  const PAGE_DA         = 'analytics'        ;
  const PAGE_DA_FILES   = 'da-files'         ;
  const PAGE_DA_RULES   = 'da-rules'         ;
  const PAGE_DA_VERIFY2 = 'da-verify2'       ;
  const PAGE_FILES      = 'files'            ;
  const PAGE_FILE       = 'file'             ;
  const PAGE_AUDIT_QC   = 'audit-qc'         ;


  const _pages = {
    [ PAGE_OCROD    ]: <OcrOnDemand      /> ,
    [ PAGE_VERIFY   ]: <TaskVerification /> ,
    [ PAGE_VERIFY_FIELDS ]: <Fields     /> ,
    [ PAGE_VERIFY_IMAGES ]: <Image      /> ,
    [ PAGE_VERIFY_THUMBS ]: <Thumbnails /> ,
    [ PAGE_WFMON    ]: <WorkflowMonitor  /> ,
    [ PAGE_STATS    ]: <Stats            /> ,
    [ PAGE_ADMIN    ]: <Admin            /> ,
    [ PAGE_DA       ]: <DocAnalytics     /> ,
    [ PAGE_FILES    ]: <FileViewer       /> ,
    [ PAGE_AUDIT_QC ]: <AuditQC          /> ,

  }

  const _paths = {
    [ PAGE_OCROD        ]: 'on-demand'   ,
    [ PAGE_VERIFY       ]: 'verify'      ,
    [ PAGE_VERIFY_FIELDS ]: 'verify/fields'    ,
    [ PAGE_VERIFY_IMAGES ]: 'verify/image'     ,
    [ PAGE_VERIFY_THUMBS ]: 'verify/thumbnails',
    [ PAGE_WFMON        ]: 'workflow'    ,
    [ PAGE_STATS        ]: 'statistics'  ,
    [ PAGE_ADMIN        ]: 'admin'       ,
    [ PAGE_DA           ]: 'analytics/*' ,
    [ PAGE_DA_FILES     ]: 'analytics/files' ,
    [ PAGE_DA_RULES     ]: 'analytics/rules/*' ,
    [ PAGE_DA_VERIFY2   ]: 'analytics/files/:file_id/task' ,
    [ PAGE_FILES        ]: 'files/*' ,
    [ PAGE_FILE         ]: 'files/:file_id' ,
    [ PAGE_AUDIT_QC     ]: 'audit-qc',

  }

  //#endregion

  //-----------------------------------------------------------------
  async function logVersionInfo() {
    //-----------------------------------------------------------------
    const response = await prokeyApi.getVersion();
    console.log('PdWebServer version: '  + response.data);
    console.log('ProkeyClient version: ' + pjson.version);
  }


  useEffect( () => {
    
    //-----------------------------------------------------------------
    function handleReconnectFail() { logout();   }
      //-----------------------------------------------------------------

    //-----------------------------------------------------------------
    function handleInactive     () { logout(); }
      //-----------------------------------------------------------------

    onReconnectFail( handleReconnectFail );
    onInactive     ( handleInactive      );
    
    logVersionInfo();

    return () => {
      removeListener( 'reconnect-fail' , handleReconnectFail );
      removeListener( 'inactive'       , handleInactive      );
    }
  }, [] );

  // SSO returns directly to a route without going through the navigation
  // drawer. Keep the stored/Redux page in sync with that route so the Verify
  // task bar and selected navigation item reflect the page being displayed.
  useEffect( () => {
    const route_page = getDisplayPage( location.pathname );
    if( route_page ) dispatch( displayActions.setPage( route_page ) );
  }, [dispatch, location.pathname] );

  //#region [DISPLAY]

  function display() {
    return (
      <Box sx={ {width: '100vw', height: '100vh', overflow: 'hidden', backgroundColor: theme.palette.panel.main } }>
        <Alert />
        <Routes>
          <Route path={ _paths[ PAGE_VERIFY        ] } element={ <Menu><TaskVerifyBar /></Menu> } />
          <Route path={ _paths[ PAGE_VERIFY_FIELDS ] } element={ <Menu><TaskVerifyBar /></Menu> } />
          <Route path={ _paths[ PAGE_VERIFY_IMAGES ] } element={ null }/>
          <Route path={ _paths[ PAGE_VERIFY_THUMBS ] } element={ null }/>
          <Route path={ _paths[ PAGE_DA            ] } element={ is_online ? null : <Menu/> } />
          <Route path={ _paths[ PAGE_FILES         ] } element={ is_online ? null : <Menu/> } />
          <Route path={ _paths[ PAGE_OCROD         ] } element={ <Menu/> } />
          <Route path={ _paths[ PAGE_WFMON         ] } element={ <Menu/> } />
          <Route path={ _paths[ PAGE_STATS         ] } element={ <Menu/> } />
          <Route path={ _paths[ PAGE_ADMIN         ] } element={ <Menu/> } />
          <Route path={ _paths[ PAGE_AUDIT_QC      ] } element={ <Menu/> } />
        </Routes>
        
        { !is_online
          ? <Styled styles={[{display: 'flex', alignItems: 'center', justifyContent: 'center'}]}><CircularProgress /></Styled>
          : <Routes>
              <Route path={ _paths[ PAGE_VERIFY   ] } element={ <TaskVerification />} />
              <Route path={ _paths[ PAGE_VERIFY_FIELDS ] } element={ <Fields     />} />
              <Route path={ _paths[ PAGE_VERIFY_IMAGES ] } element={ <Image      />} />
              <Route path={ _paths[ PAGE_VERIFY_THUMBS ] } element={ <Thumbnails />} />
              <Route path={ _paths[ PAGE_DA       ] } element={ <DocAnalytics     />} />
              <Route path={ _paths[ PAGE_OCROD    ] } element={ <OcrOnDemand      />} />
              <Route path={ _paths[ PAGE_WFMON    ] } element={ <WorkflowMonitor  />} />
              <Route path={ _paths[ PAGE_STATS    ] } element={ <Stats            />} />
              <Route path={ _paths[ PAGE_ADMIN    ] } element={ <Admin            />} />
              <Route path={ _paths[ PAGE_FILES    ] } element={ <FileViewer       />} />
              <Route path={ _paths[ PAGE_AUDIT_QC ] } element={ <AuditQC          />} />
              <Route path={ '/login'                } element={ <Login            />} />
              <Route path={ '/*'                    } element={ <Navigate to='verify' /> } />
            </Routes>
        }
      </Box>
    );
  }

  //#endregion

  return display();
}

export function getDisplayPage( pathname='' ) {
  const [page] = pathname.split('/').filter(Boolean);
  const pages = new Set([
    'on-demand', 'verify', 'workflow', 'statistics', 'admin',
    'analytics', 'files', 'audit-qc',
  ]);

  return pages.has(page) ? page : null;
}
